// The queue of the writes of each map (the Fable ruling on the order of a map's writes, R1). The API's lock has no order of its own: two requests of one
// map that wait for it together can commit in either order, and a write replaces the whole map. So the editor keeps one write of a map on the wire at a
// time. Every request that changes a map's values, and every read that would replace a loaded map, is a step on that map's queue; steps of different
// maps run side by side.
//
// A step is {kind, part, run}.
//   kind  'save', 'reload', 'undo' or 'reset'.
//   part  'data', 'X' or 'Y' for a save, null for the others.
//   run   () => Promise<void>. It never rejects by construction (the actions deal with their own failures); a throw is reported with the map and the
//         queue goes on.
//
// What is admitted: a save joins the queued save of the same part that has not started, if there is one (the step reads the values on screen when it
// runs, so there is nothing to merge: the callers share its promise). A reload is settled at once, without running, when any step is queued, because
// every queued step ends by refreshing the map from a response. Undo and reset steps are always appended.
//
// `ctx.mapWrites` is the queue's state, one per provider: a Map of mapId to {running, queue}. An entry exists only while a step runs or waits.

export const createMapWrites = () => new Map();

const entryOf = (ctx, mapId) => ctx.mapWrites.get(mapId);

// Whether a save is queued and has not started: what an outcome checks before it decides whether the table on screen is still the user's.
export const hasQueuedSave = (ctx, mapId) => {
    const entry = entryOf(ctx, mapId);
    return Boolean(entry && entry.queue.some(step => step.kind === 'save'));
};

// Whether a step of the map is running or waiting.
export const hasMapWrite = (ctx, mapId) => {
    const entry = entryOf(ctx, mapId);
    return Boolean(entry && (entry.running || entry.queue.length > 0));
};

// Runs the steps of the map, one after another, until the queue is empty, and then forgets the map. It starts the first step before it returns, so a
// step queued on an idle map has begun by the time the caller has its promise.
const run = async (ctx, mapId, entry) => {
    entry.running = true;
    while (entry.queue.length > 0) {
        const step = entry.queue.shift();
        try {
            await step.run();
        } catch (error) {
            ctx.captureException(error, mapId);
        }
        step.settle();
    }
    entry.running = false;
    ctx.mapWrites.delete(mapId);
};

/**
 * Queues a step on the map.
 * @param {Object} ctx - carries `mapWrites` and `captureException`
 * @param {string} mapId
 * @param {{kind: string, part: ?string, run: function(): Promise<void>}} step
 * @returns {Promise<void>} settles when the step has, or at once for a reload that is skipped; the same promise for the saves that joined
 */
export const enqueueMapWrite = (ctx, mapId, {kind, part = null, run: runStep}) => {
    const entry = entryOf(ctx, mapId);

    if (kind === 'save') {
        const joined = entry && entry.queue.find(step => step.kind === 'save' && step.part === part);
        if (joined) return joined.promise;
    } else if (kind === 'reload' && entry && entry.queue.length > 0) {
        return Promise.resolve();
    }

    const step = {kind, part, run: runStep};
    step.promise = new Promise(resolve => {
        step.settle = resolve;
    });

    if (entry) {
        entry.queue.push(step);
    } else {
        const created = {running: false, queue: [step]};
        ctx.mapWrites.set(mapId, created);
        run(ctx, mapId, created);
    }

    return step.promise;
};

/**
 * Settles and removes every step of the map that has not started, for a map the release lacks: each would find the same 404. The step that is running
 * goes on.
 * @param {Object} ctx
 * @param {string} mapId
 */
export const dropQueuedWrites = (ctx, mapId) => {
    const entry = entryOf(ctx, mapId);
    if (!entry) return;

    entry.queue.splice(0).forEach(step => step.settle());
};
